import { forwardRef } from "react";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "../../utils/cn";
import { Icon } from "./Icon";

export type ModalCloseButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "children" | "aria-label"
> & {
  /** Accessible label announced to screen readers. Defaults to "Close". */
  label?: string;
  /** Overrides the default lucide:x glyph size class (default "h-4 w-4"). */
  iconClassName?: string;
};

/**
 * Shared header close ("X") control for modals, dialogs, popovers and flyouts.
 *
 * Bakes in the canonical enhanced focus-visible ring so every dismissal control
 * is keyboard-visible by default, plus type="button", the neutral hover wash and
 * an aria-label. Pass `className` to override the box size or hover treatment for
 * a specific surface -- cn()/twMerge lets the later class win.
 */
export const ModalCloseButton = forwardRef<HTMLButtonElement, ModalCloseButtonProps>(
  ({ label = "Close", iconClassName, className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground",
        "hover:bg-white/[0.06] transition-colors disabled:opacity-40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        className,
      )}
      {...props}
    >
      <Icon icon="lucide:x" className={cn("h-4 w-4", iconClassName)} />
    </button>
  ),
);

ModalCloseButton.displayName = "ModalCloseButton";
